import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default class LogoLayerDirector {
    constructor(layer) {
        this.logoLayer = layer;

        this.handlingTransition = false;

        this.currentState = 'init';
        setInterval(()=> {
            this.checkTargetState();
        }, 100);

        this.currentCode = '';
        setInterval(()=> {
            this.checkTargetCode();
        }, 100);

        const mm = gsap.matchMedia();
		mm.add(
			{
				isMobile: `(max-width: 991px)`,
				isDesktop: `(min-width: 992px)`,
				reduceMotion: '(prefers-reduced-motion: reduce)',
			}, (context) => {
                this.context = context;
                this.resize(context);
			}
		);

        this.boundBlessListener = this.blessListener.bind(this);
		document.addEventListener('bless', this.boundBlessListener);
    }

    blessListener(e) {
        if(this.handlingTransition) {
            this.blessEvent = e;
            return;
        } else {
            this.blessEvent = null;
        }

        const container = e.detail.element;

        this.host = container.querySelector('[data-logo-layer-host]');
        let hostClass = '';
        if(this.host) {
            hostClass = this.host.getAttribute('data-logo-layer-host');
        }

        if(this.host) {
            ScrollTrigger.create({
                trigger: this.host,
                start: "top top",
                end: "center top",
                onEnter: () => {
                    this.enterHost();
                    this.logoLayer.detachFromTop(hostClass);
                },
                onEnterBack: () => {
                    this.enterHost();
                    this.logoLayer.detachFromTop(hostClass);
                },
                onLeave: () => {
                    this.leaveHost();
                    this.logoLayer.attachToTop(hostClass);
                },
                onLeaveBack: () => {
                    this.leaveHost();
                    this.logoLayer.detachFromTop(hostClass);
                }
            });
        }

    }

    handleTransition() {

        this.handlingTransition = true;

        this.returnToHostAfterMenuClose = false;
        if(this.logoLayer.hasHost) {
            const top = this.logoLayer.elm.getBoundingClientRect().top;
            gsap.set(this.logoLayer.elm, {
                top: top+'px',
                onComplete: () => {
                    this.leaveHost();
                    this.logoLayer.returnToCenter().then(() => {
                        this.logoLayer.detachFromTop();
                        this.handlingTransition = false;
                        if(this.blessEvent) {
                            this.blessListener(this.blessEvent);
                        }
                    });
                }
            });
        } else if(this.logoLayer.isOnTop) {
            this.logoLayer.returnToCenter().then(() => {
                this.logoLayer.detachFromTop();
                this.handlingTransition = false;
                if(this.blessEvent) {
                    this.blessListener(this.blessEvent);
                }
            });
        } else {
            this.logoLayer.returnToCenter(0.8).then(() => {
                this.logoLayer.detachFromTop();
                this.handlingTransition = false;
                if(this.blessEvent) {
                    this.blessListener(this.blessEvent);
                }
            });
        }
    }

    enterHost() {
        if(this.host) {
            this.host.appendChild(this.logoLayer.elm);
        }
        this.logoLayer.hasHost = true;
    }

    leaveHost() {
        document.body.appendChild(this.logoLayer.elm);
        this.logoLayer.hasHost = false;
    }

    returnToCenter(duration, samePage) {
        if(this.isInfo) {
            this.goToInfo(0.8);
        } else {
            this.logoLayer.returnToCenter(duration, samePage).then(() => {
                if(this.returnToHostAfterMenuClose) {
                    this.enterHost();
                    this.returnToHostAfterMenuClose = false;
                }
            });
        }
    }

    makeRoomForMenu(menu) {
        let menuHeight = menu.elm.getBoundingClientRect().height;
        if(menu.isOpen&&this.context.conditions.isMobile) {

            this.logoLayer.prevTop = this.logoLayer.elm.getBoundingClientRect().top;

            // Temporarily leave host to make sure to stay above blur layer
            if(this.logoLayer.hasHost) {
                this.returnToHostAfterMenuClose = true;
                gsap.set(this.logoLayer.elm, {
                    top: this.logoLayer.prevTop,
                    onComplete: () => {
                        this.leaveHost();
                    }
                });
            }

            const svh = document.documentElement.clientHeight;
            const dvh = window.innerHeight;
            const vhDiff = dvh-svh;
            let targetTop = (menuHeight-(dvh/2))+((dvh-menuHeight)/2)+(vhDiff/2);

            this.logoLayer.makeRoomForMenu(targetTop);
        }
    }

    checkTargetCode() {
        if(this.currentCode!=this.targetCode) {
            this.newCode(this.targetCode);
        }
    }

    newCode(targetCode) {

        if(typeof(targetCode)=='undefined') {
            targetCode = '';
        }

        this.targetCode = targetCode;

        if(this.currentCode==targetCode) {
            return;
        }

        if(this.currentState=='small'&&this.targetCode) {
            this.setTargetState('code').then(() => {
                this.currentCode = targetCode;
                this.codeAnimating = false;
            });
            return;
        }

        if(this.currentState!='code') {
            return;
        }

        if(this.codeAnimating) {
            return;
        }

        new Promise(resolve => {
            this.codeAnimating = true;
            if(targetCode=='') {
                return this.setTargetState('small').then(() => {
                    resolve();
                });
            } else if(this.currentCode=='') {
                return this.setTargetState('code').then(() => {
                    resolve();
                });

            } else {
                return this.logoLayer.updateCode(targetCode).then(resolve);
            }
        }).then(() => {
            this.currentCode = targetCode;
            this.codeAnimating = false;
        });

    }

    resize(context) {
        this.logoLayer.resize(context);

        if(this.currentState=='animating') {
            this.currentState = this.targetState;
        }

        document.body.classList.remove('is-animating');
        this.renderState();
    }

    renderState() {
        switch(this.currentState) {
            case 'code':
                this.currentCode = this.targetCode;
                this.logoLayer.renderCode(this.currentCode);
                this.codeAnimating = false;
                break;
        }
    }

    setTargetState(targetState) {
        return new Promise(resolve => {
            this.targetState = targetState;
            this.checkTargetState().then(resolve);
        });
    }

    checkTargetState() {
        return new Promise(resolve => {
            if(this.currentState==this.targetState) {
                resolve();
                return;
            }
            if(this.currentState=='animating') {
                resolve();
                return;
            }
            this.transitionToState(this.currentState, this.targetState).then(resolve);
        });
    }

    transitionToState(currentState, targetState) {
        const logoLayer = this.logoLayer;
        const director = this;

        const transitions = {
			init: {
				preloader() {
                    return logoLayer.goInitToPreloader();
                },
			},
			preloader: {
				small() {
                    setTimeout(() => {
                        document.body.classList.remove('is-animating');
                    }, 1200);
                    return logoLayer.goPreloaderToSmall();
                },
                medium() {
                    return logoLayer.goPreloaderToSmall().then(() => {
                        if(director.currentState=='animating') {
                            document.body.classList.remove('is-animating');
                            return logoLayer.goSmallToMedium();
                        }
                    });
                },
                full() {
                    return logoLayer.goPreloaderToSmall().then(() => {
                        if(director.currentState=='animating') {
                            document.body.classList.remove('is-animating');
                            return logoLayer.goSmallToFull();
                        }
                    });
                }
			},
            small: {
				preloader() {
                    director.newCode('');
                    return logoLayer.goSmallToPreloader();
                },
                medium() {
                    document.body.classList.remove('is-animating');
                    return logoLayer.goSmallToMedium();
                },
                full() {
                    document.body.classList.remove('is-animating');
                    return logoLayer.goSmallToFull();
                },
                code() {
                    document.body.classList.remove('is-animating');
                    return logoLayer.smallToCode(director.targetCode).then(() => {
                        director.currentCode = director.targetCode;
                    });
                }
			},
            medium: {
                preloader() {
                    return logoLayer.goMediumToSmall().then(() => {
                        if(director.currentState=='animating') {
                            director.newCode('');
                            return logoLayer.goSmallToPreloader();
                        }
                    });
                },
                small() {
                    return logoLayer.goMediumToSmall();
                },
                full() {
                    return logoLayer.goMediumToSmall().then(() => {
                        if(director.currentState=='animating') {
                            document.body.classList.remove('is-animating');
                            return logoLayer.goSmallToFull();
                        }
                    });
                }
			},
            full: {
				small() {
                    return logoLayer.goFullToSmall();
                },
                medium() {
                    return logoLayer.goFullToSmall().then(() => {
                        if(director.currentState=='animating') {
                            document.body.classList.remove('is-animating');
                            return logoLayer.goSmallToMedium();
                        }
                    });
                },
                preloader() {
                    return logoLayer.goFullToSmall().then(() => {
                        if(director.currentState=='animating') {
                            director.newCode('');
                            return logoLayer.goSmallToPreloader();
                        }
                    });
                }
			},
            code: {
                preloader() {
                    return logoLayer.codeToSmall().then(() => {
                        director.currentCode = null;
                        if(director.currentState=='animating') {
                            return logoLayer.goSmallToPreloader();
                        }
                    });
                },
                small() {
                    return logoLayer.codeToSmall().then(() => {
                        director.currentCode = null;
                    });
                },
                code() {
                    return logoLayer.updateCode(director.targetCode).then(() => {
                        director.currentCode = director.targetCode;
                    });
                }
            }
		};
		const transition = transitions[currentState][targetState];

        if(!transition) {
            console.error('Transition not found from:', currentState, ' to ',targetState);
            return;
        }

        return new Promise(resolve => {
            this.currentState = 'animating';
            transition().then(() => {
                resolve();
                this.currentState = targetState;
                this.checkTargetState();
            });

        });
    }

    goToInfo(duration) {
        this.isInfo = true;
        this.logoLayer.makeRoomForInfo(duration);
    }

    leaveInfo() {
        this.isInfo = false;
        this.returnToCenter(0.8);
    }

}